import { activeNavHref } from '@odo/shared-ui';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SidebarFlyout, useAnchoredFlyout } from '@odo/shared-ui';
import accountIcon from '../../assets/icons/account.svg';
import bookOpenIcon from '../../assets/icons/book-open.svg';
import dollarIcon from '../../assets/icons/dollar.svg';
import homeIcon from '../../assets/icons/home.svg';
import logoutIcon from '../../assets/icons/log-out.svg';
import logo from '../../assets/icons/odo-logo-res.png';
import messagesIcon from '../../assets/icons/messages-square.svg';
import shopIcon from '../../assets/icons/shop.svg';

import { usePatientMetadata } from '../../providers/PatientMetadataProvider';
import { useUser } from '../../providers/UserProvider';
import { hasBetaFeature } from '../../utils/betaFeatures';
import { SUPPORT_PHONE } from '../../utils/constants';

const BILLING_BETA_FEATURE = 'billing';

const HeadsetIcon = ({ size = 13, color = '#6B6862' }: { size?: number; color?: string }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 20 20"
        fill="none"
        stroke={color}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 10a7 7 0 0114 0" />
        <rect x="1" y="10" width="4" height="6" rx="2" />
        <rect x="15" y="10" width="4" height="6" rx="2" />
        <path d="M19 16v1a3 3 0 01-3 3h-3" />
    </svg>
);

interface NavItem {
    icon: React.ReactNode;
    label: string;
    badge?: string;
    href?: string;
    disabled?: boolean;
    subItems?: NavItem[];
}

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className
}) => <span className={className}>{children}</span>;

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

// Module-level so the component body stays focused on behavior.
function buildNavItems(inputs: {
    homeBadge: string;
    messagesBadge: string;
    showBilling: boolean;
}): NavItem[] {
    return [
        {
            icon: <img src={homeIcon} alt="Home" className="h-4 w-4" />,
            label: 'Home',
            badge: inputs.homeBadge,
            href: '/home'
        },
        {
            icon: <img src={messagesIcon} alt="Messages" className="h-4 w-4" />,
            label: 'Messages',
            badge: inputs.messagesBadge,
            href: '/conversations'
        },
        {
            icon: <img src={shopIcon} alt="Shop" className="h-4 w-4" />,
            label: 'Shop',
            href: '/shop'
        },
        // Beta-gated: hidden until the practice has the 'billing' flag
        // (backend PracticeBetaFeature, admin-managed).
        ...(inputs.showBilling
            ? [
                  {
                      icon: <img src={dollarIcon} alt="Billing" className="h-4 w-4" />,
                      label: 'Billing',
                      href: '/billing'
                  }
              ]
            : []),
        {
            icon: <img src={bookOpenIcon} alt="Resources" className="h-5 w-5" />,
            label: 'Resources',
            subItems: [
                { label: 'Overview', href: '/resources', icon: <></> },
                { label: 'Help Center', href: '/resources/help-center', icon: <></> },
                { label: 'Videos', href: '/resources/videos', icon: <></> },
                { label: 'FAQ', href: '/resources/faq', icon: <></> }
            ]
        },
        {
            icon: <img src={accountIcon} alt="Account" className="h-4 w-4" />,
            label: 'Account',
            href: '/account'
        }
    ];
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState<string | null>(
        location.pathname.startsWith('/resources') ? 'Resources' : null
    );

    // Collapsed-mode flyout submenu; the shared hook owns open/dismiss
    // (Escape, outside click, resize, re-expand). asideRef marks the host.
    const {
        flyout,
        toggle: toggleFlyout,
        close: closeFlyout,
        containerRef: asideRef
    } = useAnchoredFlyout<HTMLElement>({ enabled: collapsed, closeOnResize: true });
    const { user, getUnreadCount, logout } = useUser();
    const { unreadPatientIds } = usePatientMetadata();
    const unreadMessagesCount = unreadPatientIds.size;
    const showBilling = hasBetaFeature(user, BILLING_BETA_FEATURE);

    const navItems = buildNavItems({
        homeBadge: getUnreadCount().toString(),
        messagesBadge: unreadMessagesCount.toString(),
        showBilling
    });

    const handleNavClick = (item: NavItem, event: React.MouseEvent<HTMLButtonElement>) => {
        if (item.disabled) return;
        if (item.subItems) {
            if (collapsed) {
                const rect = event.currentTarget.getBoundingClientRect();
                toggleFlyout(item.label, { left: rect.right, top: rect.top });
            } else {
                setOpenMenu(openMenu === item.label ? null : item.label);
            }
        } else if (item.href) {
            closeFlyout();
            navigate(item.href);
        }
    };

    const flyoutItem = flyout ? navItems.find((i) => i.label === flyout.key) : null;
    const flyoutSubItems = (flyoutItem?.subItems ?? []).flatMap((s) =>
        s.href ? [{ label: s.label, href: s.href, soon: s.disabled }] : []
    );

    const handleLogout = () => {
        // Navigate first: clearing the cache under mounted queries makes their
        // observers refetch without a token, and each 401 hard-redirects.
        navigate('/login');
        logout();
    };

    return (
        <aside
            ref={asideRef}
            className={`flex flex-col h-full bg-white border-r border-gray-200 shadow-sm transition-all duration-300 ${collapsed ? 'w-[60px]' : 'w-[220px]'}`}
        >
            <div className="flex flex-col items-start gap-10 px-2 pt-4 w-full flex-1">
                {/* Logo + toggle */}
                <div
                    className={`flex items-center w-full ${collapsed ? 'flex-col gap-3' : 'justify-between px-2'}`}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        className={`object-contain cursor-pointer ${collapsed ? 'w-8 h-8' : 'w-[95px] h-10'}`}
                        onClick={() => navigate('/home')}
                    />
                    <button
                        onClick={onToggle}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 20 20"
                            fill="none"
                            stroke="#9ca3af"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={`transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
                        >
                            <rect x="2" y="2" width="16" height="16" rx="3" />
                            <line x1="8" y1="2" x2="8" y2="18" />
                            <polyline points="14,8 11,10 14,12" />
                        </svg>
                    </button>
                </div>

                {/* Navigation Menu */}
                <nav className="flex flex-col items-start gap-1.5 w-full">
                    {navItems.map((item, index) => {
                        // Collapsed parents light up from an active sub-route or open flyout.
                        const isFlyoutOpen = collapsed && flyout?.key === item.label;
                        const isActive = item.href
                            ? location.pathname === item.href ||
                              location.pathname.startsWith(item.href + '/')
                            : collapsed && item.subItems
                              ? isFlyoutOpen ||
                                item.subItems.some(
                                    (s) =>
                                        s.href !== undefined &&
                                        (location.pathname === s.href ||
                                            location.pathname.startsWith(s.href + '/'))
                                )
                              : false;
                        const isMenuOpen = !collapsed && openMenu === item.label;
                        const hasBadge = item.badge && parseInt(item.badge) > 0;
                        // Longest match wins, so "Overview" (/resources) does not
                        // stay lit while a child section is open.
                        const activeSubHref = item.subItems
                            ? activeNavHref(
                                  location.pathname,
                                  item.subItems.flatMap((sub) => sub.href ?? [])
                              )
                            : undefined;

                        return (
                            <React.Fragment key={index}>
                                <div className="relative group w-full">
                                    <button
                                        onClick={(e) => handleNavClick(item, e)}
                                        aria-haspopup={item.subItems ? 'true' : undefined}
                                        aria-expanded={
                                            item.subItems ? isFlyoutOpen || isMenuOpen : undefined
                                        }
                                        title={isFlyoutOpen ? undefined : item.label}
                                        className={`flex h-10 items-center gap-2 px-3 py-2 w-full rounded-lg transition-colors duration-200 overflow-hidden ${item.disabled ? 'opacity-50' : 'hover:bg-gray-100'} ${collapsed ? 'justify-center' : ''} ${isActive ? 'bg-gray-200' : ''}`}
                                        disabled={item.disabled}
                                        aria-disabled={item.disabled}
                                    >
                                        <div className="flex w-5 h-5 items-center justify-center text-gray-600 flex-shrink-0 relative">
                                            {item.icon}
                                            {collapsed && hasBadge && (
                                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-yellow-400 rounded-full border border-white" />
                                            )}
                                        </div>

                                        {!collapsed && (
                                            <>
                                                <span
                                                    className={`text-sm truncate min-w-0 ${
                                                        isActive
                                                            ? 'font-semibold text-gray-900'
                                                            : 'font-medium text-gray-700'
                                                    }`}
                                                >
                                                    {item.label}
                                                </span>

                                                {hasBadge && (
                                                    <Badge className="bg-yellow-400 text-gray-800 px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-auto">
                                                        {item.badge}
                                                    </Badge>
                                                )}
                                            </>
                                        )}
                                    </button>
                                    {item.disabled && (
                                        <div className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-md opacity-0 group-hover:opacity-100">
                                            Coming soon
                                        </div>
                                    )}
                                </div>
                                {/* Submenu (expanded mode only) */}
                                <div
                                    className="transition-all duration-300 ease-in-out overflow-hidden w-full"
                                    style={{
                                        maxHeight:
                                            isMenuOpen && item.subItems
                                                ? `${item.subItems.length * 40}px`
                                                : '0px'
                                    }}
                                >
                                    {item.subItems && (
                                        <div
                                            className={`flex w-full transition-opacity duration-300 ${
                                                isMenuOpen ? 'opacity-100' : 'opacity-0'
                                            }`}
                                        >
                                            <div className="w-5 flex justify-center ml-4 relative">
                                                <div className="w-[2px] bg-gray-300 absolute top-0 bottom-0"></div>
                                            </div>
                                            <div className="flex flex-col w-full gap-2">
                                                {item.subItems.map((subItem, subIndex) => {
                                                    const isSubActive =
                                                        subItem.href != null &&
                                                        subItem.href === activeSubHref;
                                                    return (
                                                        <div
                                                            key={subIndex}
                                                            className="relative group"
                                                        >
                                                            <button
                                                                onClick={(e) =>
                                                                    handleNavClick(subItem, e)
                                                                }
                                                                className={`flex h-8 items-center px-3 w-full rounded-lg transition-colors duration-200 ${subItem.disabled ? 'opacity-50' : 'hover:bg-gray-100'} ${isSubActive ? 'bg-gray-200' : ''}`}
                                                                disabled={subItem.disabled}
                                                                aria-disabled={subItem.disabled}
                                                            >
                                                                <span
                                                                    className={`text-sm truncate min-w-0 ${
                                                                        isSubActive
                                                                            ? 'font-semibold text-gray-900'
                                                                            : 'font-medium text-gray-700'
                                                                    }`}
                                                                >
                                                                    {subItem.label}
                                                                </span>
                                                            </button>
                                                            {subItem.disabled && (
                                                                <div className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-md opacity-0 group-hover:opacity-100">
                                                                    Coming soon
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </React.Fragment>
                        );
                    })}
                </nav>
            </div>

            {/* Collapsed-mode flyout submenu (portalled to body) */}
            {flyout?.anchor && flyoutItem && flyoutSubItems.length > 0 && (
                <SidebarFlyout
                    label={flyoutItem.label}
                    icon={flyoutItem.icon}
                    subItems={flyoutSubItems}
                    anchor={flyout.anchor}
                    currentPath={location.pathname}
                    onSelect={(href) => {
                        navigate(href);
                        closeFlyout();
                    }}
                />
            )}

            {/* Support card + Logout */}
            <div className="px-2 pb-6 flex flex-col gap-1">
                <div className="border-t border-gray-200 mb-2" />
                {collapsed ? (
                    <a
                        href={`tel:${SUPPORT_PHONE.tel}`}
                        aria-label="Call support"
                        title={SUPPORT_PHONE.display}
                        className="flex h-10 items-center justify-center w-full rounded-lg hover:bg-gray-100 transition-colors duration-200"
                    >
                        <HeadsetIcon size={16} color="#6B6862" />
                    </a>
                ) : (
                    <a
                        href={`tel:${SUPPORT_PHONE.tel}`}
                        aria-label="Call support"
                        title={SUPPORT_PHONE.display}
                        style={{
                            background: '#F5F4F0',
                            border: '1px solid #ECEAE3',
                            borderRadius: 10,
                            padding: '10px 12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            textDecoration: 'none',
                            transition: 'background 200ms'
                        }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = '#EFEDE8';
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.background = '#F5F4F0';
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <HeadsetIcon size={13} color="#6B6862" />
                            <span
                                style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: '#6B6862',
                                    letterSpacing: '-0.005em'
                                }}
                            >
                                Need urgent help?
                            </span>
                        </div>
                        <span
                            style={{
                                color: '#1F1D1A',
                                fontSize: 13,
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2
                            }}
                        >
                            {SUPPORT_PHONE.display}
                        </span>
                    </a>
                )}
                <div className="border-t border-[#ECEAE3] my-2 w-full" />
                <button
                    onClick={handleLogout}
                    title="Log out"
                    className={`flex h-10 items-center gap-2 px-3 py-2 w-full rounded-lg transition-colors duration-200 hover:bg-gray-100 text-gray-700 ${collapsed ? 'justify-center' : ''}`}
                >
                    <img src={logoutIcon} alt="Logout" className="h-4 w-4 flex-shrink-0" />
                    {!collapsed && <span className="text-sm font-medium">Log out</span>}
                </button>
            </div>
        </aside>
    );
};
